/**
 * hfpFraming.js
 * -------------
 * The framing line shown above each profile (HFP) statement, made to speak
 * about the actual child. The form definitions store the framing generically
 * ("Compared to other children their age, how well can they:"); the design
 * writes it with the child's name ("Compared to other children Freddie's
 * age, how does Freddie manage the following?").
 *
 * Personalisation is an explicit map from each stored framing to its
 * personalised form, never a blanket find-and-replace, so verb agreement is
 * right ("how does Freddie", not "how do Freddie") and a framing nobody has
 * mapped falls back to its stored text rather than being mangled.
 *
 * Some stored framings are authoring notes (scoring rules, internal flags)
 * that were never meant to be read by a parent or a school. They are not
 * shown at all.
 */

const POSSESSIVE_PRONOUNS = {
  he_him: 'his',
  she_her: 'her',
  they_them: 'their',
};

// Stored framing -> personalised framing. {name} is the child's first name,
// {names} its possessive, {pos} the possessive pronoun (his / her / their).
const PERSONALISED = {
  // HFP v3
  'Compared to other children their age, how well can they:':
    'Compared to other children {names} age, how well can {name}:',
  'Compared to other children their age, how well do they tend to:':
    'Compared to other children {names} age, how well does {name} tend to:',
  'In their daily life, how well do these meet their needs?':
    'In {names} daily life, how well do these meet {pos} needs?',
  'Compared with other children their age, how strongly do they usually react to:':
    'Compared with other children {names} age, how strongly does {name} usually react to:',
  // HFP v2 (the wording the question frames write out in full)
  'Compared to other children their age, how do they manage the following?':
    'Compared to other children {names} age, how does {name} manage the following?',
};

// Authoring notes stored in the framing slot: never shown to families or schools.
const AUTHOR_NOTE_PREFIXES = ['Asked only when', 'Conditional —', 'Conditional -'];

export function isAuthorNote(framing) {
  const text = String(framing || '').trim();
  return AUTHOR_NOTE_PREFIXES.some((prefix) => text.startsWith(prefix));
}

function possessive(name) {
  return `${name}'s`;
}

/**
 * Replace a stored [child name] placeholder with the child's first name, or
 * with "your child" ("their" for the possessive) when no name is known, so the
 * placeholder never reaches the screen.
 *
 * @param {string} text
 * @param {string|null|undefined} firstName
 * @returns {string}
 */
export function fillChildName(text, firstName) {
  if (typeof text !== 'string' || !text.includes('[child name]')) return text;
  const name = String(firstName || '').trim();
  if (!name) return text.replace(/\[child name\]'s/g, 'their').replace(/\[child name\]/g, 'your child');
  return text.replace(/\[child name\]/g, name);
}

const CHILD_NAME_FIELDS = ['label', 'description', 'title', 'helpText'];

/**
 * A copy of a form schema with every [child name] placeholder in its wording
 * (question labels, hints, option labels, section titles) filled by
 * fillChildName. Ids and structure are untouched. Returns the schema itself
 * when it carries no placeholder.
 *
 * @param {object|null} schema
 * @param {string|null|undefined} firstName
 * @returns {object|null}
 */
export function fillSchemaChildName(schema, firstName) {
  if (!schema || !JSON.stringify(schema).includes('[child name]')) return schema;
  const walk = (node) => {
    if (Array.isArray(node)) return node.map(walk);
    if (!node || typeof node !== 'object') return node;
    const out = {};
    Object.keys(node).forEach((key) => {
      const value = node[key];
      out[key] = CHILD_NAME_FIELDS.includes(key) && typeof value === 'string'
        ? fillChildName(value, firstName)
        : walk(value);
    });
    return out;
  };
  return walk(schema);
}

/**
 * Personalise a stored framing line for a named child.
 *
 * @param {string} framing   the stored framing text
 * @param {object} child     { firstName, pronouns } (pronouns like 'she_her')
 * @param {object} [options] { personaliseGeneric } — false keeps mapped
 *   generic wording as stored (pages whose copy has not been through the
 *   gates); author notes are still hidden and [child name] still filled.
 * @returns {string} the line to show ('' when nothing should be shown)
 */
export function personaliseFraming(framing, { firstName, pronouns } = {}, { personaliseGeneric = true } = {}) {
  const text = String(framing || '').trim();
  if (!text || isAuthorNote(text)) return '';
  const name = String(firstName || '').trim();

  // A stored [child name] placeholder must never reach the screen.
  if (text.includes('[child name]')) return fillChildName(text, name);

  const template = PERSONALISED[text];
  if (!personaliseGeneric || !template || !name) return text;
  const pos = POSSESSIVE_PRONOUNS[pronouns] || 'their';
  return template
    .replace(/\{names\}/g, possessive(name))
    .replace(/\{name\}/g, name)
    .replace(/\{pos\}/g, pos);
}
